import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Button, Form, FormGroup, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { TemplateFieldV2 } from "../../../../docuclipper/DocuclipperTypes";
import { withTooltip } from "../../../../hoc/withTooltip";
import { ReduxState } from "../../../../redux";
import {
  fetchExtractedData,
  setAlignMiddleMultilineField,
  setAlignMiddleReferenceField,
  setAlignMiddleSeparator,
  updateAlignMiddleEnabled,
  updateVerticalMergeMasks,
  updateVerticalMergeMasksEnabled,
} from "../../../../redux/reducers/TemplateField";
import PopoverHelp from "../../../Docuclipper/PopoverHelp";
import { withSelectedField } from "./withSelectedField";

const ButtonWithTT = withTooltip(Button);

const renderTooltip = (mode) => () => {
  switch (mode) {
    case 0:
      return <span>Column with text</span>;
    case 1:
      return <span>Empty column</span>;
    case 2:
      return <span>With text or empty</span>;
  }
  return <></>;
};

const Mask: React.FC<{ mode: number; onModeChange: (mode: number) => any }> = ({
  mode,
  onModeChange,
  children,
}) => {
  const toggle = () => {
    let newMode = mode;
    switch (mode) {
      case 0:
        newMode = 1;
        break;
      case 1:
        newMode = 2;
        break;
      case 2:
        newMode = 0;
        break;
    }
    onModeChange(newMode);
  };
  let icon: IconProp = "check";
  switch (mode) {
    case 0:
      icon = "check";
      break;
    case 1:
      icon = "times";
      break;
    case 2:
      icon = "question-circle";
      break;
  }
  return (
    <ButtonWithTT
      style={{ width: "100%" }}
      placement="bottom"
      id=""
      variant={`${mode === 1 ? "outline-" : ""}secondary` as any}
      renderTooltip={renderTooltip(mode)}
      onClick={toggle}
    >
      {children}
      <FontAwesomeIcon className="ml-1" icon={icon} />
    </ButtonWithTT>
  );
};

type Props = {
  onModeChange: (which: "prev" | "next", index: number, value: number) => any;
  field: TemplateFieldV2;
};

export const VerticalMerge2: React.SFC<Props> = ({ onModeChange, field }) => {
  const { tableConfig } = field;
  const [enabled, setEnabled] = React.useState(false);

  const { token } = useSelector((state: ReduxState) => state.Auth);
  const isAdmin =
    token && token.permissions && token.permissions.includes("READ_DB_TABLES");

  const dispatch = useDispatch();
  return (
    <>
      <div className="ml-3">
        <Row>
          <Form.Check
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              if (e.target.checked) {
                dispatch(
                  updateVerticalMergeMasksEnabled({
                    id: field.id,
                    enabled: true,
                  })
                );
              } else {
                dispatch(
                  updateVerticalMergeMasksEnabled({
                    id: field.id,
                    enabled: false,
                  })
                );
                dispatch(
                  updateAlignMiddleEnabled({
                    id: field.id,
                    enabled: false,
                  })
                );
              }
            }}
          />
          Merge multi-line fields
          <PopoverHelp header="" id="verticalMergeHelp" placement="bottom">
            Merge multi-line fields into a single line. The merge is performed
            if the columns match the pattern specified here.
          </PopoverHelp>
        </Row>
      </div>
      {/* top vs middle */}
      {/* [X] Align top  [] Align middle

Align top
Jan 10th  Line 1 $100
          line 2
Jan 11th  Line 1 $299
          line 2

Align middle

          Line 1 
Jan 10th          $100
          line 2
          Line 1 
Jan 11th          $299
          line 2 */}

      {enabled && (
        <>
          <div className="mb-3">
            <Form.Check
              type="radio"
              label="Align top"
              checked={field.tableConfig.transforms.verticalMergeMasksEnabled}
              onChange={(e) =>
                dispatch(
                  updateVerticalMergeMasksEnabled({
                    id: field.id,
                    enabled: e.target.checked,
                  })
                )
              }
            />

            <Form.Check
              type="radio"
              label="Align middle"
              checked={field.tableConfig.transforms.alignMiddleEnabled}
              onChange={(e) =>
                dispatch(
                  updateAlignMiddleEnabled({
                    id: field.id,
                    enabled: e.target.checked,
                  })
                )
              }
            />
          </div>

          {tableConfig.transforms.verticalMergeMasksEnabled && (
            <>
              {isAdmin && (
                <Form.Control
                  as="select"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                      if (val === "0-1") {
                        onModeChange("prev", 0, 0);
                        onModeChange("prev", 1, 0);
                        onModeChange("next", 0, 1);
                        onModeChange("next", 1, 0);
                        for (
                          let i = 2;
                          i < tableConfig.columns.length;
                          i += 1
                        ) {
                          onModeChange("prev", i, 2);
                          onModeChange("next", i, 2);
                        }
                      } else if (val === "0-2") {
                        onModeChange("prev", 0, 0);
                        onModeChange("prev", 1, 0);
                        onModeChange("prev", 2, 0);
                        onModeChange("next", 0, 1);
                        onModeChange("next", 1, 1);
                        onModeChange("next", 2, 0);
                        for (
                          let i = 3;
                          i < tableConfig.columns.length;
                          i += 1
                        ) {
                          onModeChange("prev", i, 2);
                          onModeChange("next", i, 2);
                        }
                      }
                    }
                  }}
                >
                  {["", "0-1"].map((option) => (
                    <option value={option}>{option}</option>
                  ))}
                </Form.Control>
              )}

              <div style={{ overflowX: "scroll" }}>
                <Table>
                  <thead>
                    <tr>
                      {tableConfig.columns.map((col, i) => (
                        <td>{col.name || `Column ${i + 1}`}</td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {tableConfig.columns.map((col, i) => {
                        if (!tableConfig.transforms.verticalMergeMasks) {
                          return <></>;
                        }
                        const prevMode =
                          tableConfig.transforms.verticalMergeMasks.prev[i];

                        return (
                          <td>
                            <Mask
                              mode={prevMode}
                              onModeChange={(value) =>
                                onModeChange("prev", i, value)
                              }
                            >
                              {prevMode === 0 ? "Line 1" : ""}
                            </Mask>
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      {tableConfig.columns.map((col, i) => {
                        if (!tableConfig.transforms.verticalMergeMasks) {
                          return <></>;
                        }
                        const nextMode =
                          tableConfig.transforms.verticalMergeMasks.next[i];
                        return (
                          <td>
                            <Mask
                              mode={nextMode}
                              onModeChange={(value) =>
                                onModeChange("next", i, value)
                              }
                            >
                              {nextMode === 0 ? "Line 2" : ""}
                            </Mask>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {tableConfig.transforms.alignMiddleEnabled && (
            <div>
              {
                <div>
                  <FormGroup>
                    <Form.Label>
                      <strong>Reference field</strong>
                    </Form.Label>
                    <Select
                      options={field.tableConfig.columns.map((x, i) => ({
                        value: i.toString(),
                        label: i.toString(),
                      }))}
                      value={
                        field.tableConfig.transforms.alignMiddle
                          ? {
                              value: field.tableConfig.transforms.alignMiddle.referenceFieldIndex.toString(),
                              label: field.tableConfig.transforms.alignMiddle.referenceFieldIndex.toString(),
                            }
                          : null
                      }
                      onChange={(value) => {
                        dispatch(
                          setAlignMiddleReferenceField({
                            id: field.id,
                            referenceFieldIndex: value ? value.value : "",
                          })
                        );
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Form.Label>
                      <strong>Multiline fields</strong>
                    </Form.Label>
                    <Select
                      // placeholder={`Filter by ${name}`}
                      options={field.tableConfig.columns.map((x, i) => ({
                        value: i.toString(),
                        label: i.toString(),
                      }))}
                      value={
                        field.tableConfig.transforms.alignMiddle
                          ? (field.tableConfig.transforms.alignMiddle
                              .multilineFieldIndex as any[]).map((x) => ({
                              value: x,
                              label: x,
                            }))
                          : []
                      }
                      onChange={(value) => {
                        dispatch(
                          setAlignMiddleMultilineField({
                            id: field.id,
                            multilineFieldIndex: value
                              ? value.map((x) => parseInt(x.value, 10))
                              : [],
                          })
                        );
                      }}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      // hideSelectedOptions={false}
                      allowSelectAll={true}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Form.Label>
                      <strong>Separator</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder=""
                      value={
                        field.tableConfig.transforms.alignMiddle
                          ? field.tableConfig.transforms.alignMiddle.separator
                          : ""
                      }
                      onChange={(e) =>
                        dispatch(
                          setAlignMiddleSeparator({
                            id: field.id,
                            separator: e.target.value,
                          })
                        )
                      }
                    />
                  </FormGroup>
                </div>
              }
            </div>
          )}
        </>
      )}
    </>
  );
};

const VerticalMerge_: React.FC<{ field: TemplateFieldV2 }> = ({ field }) => {
  const dispatch = useDispatch();
  return (
    <VerticalMerge2
      field={field}
      onModeChange={(which, index, value) => {
        dispatch(
          updateVerticalMergeMasks({
            id: field.id,
            index,
            which,
            value: value as 0 | 1 | 2,
          })
        );
        dispatch(fetchExtractedData({ id: field.id }));
      }}
    />
  );
};

export const VerticalMerge = withSelectedField(VerticalMerge_);
